import { ModuleLayout, notify, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { CustomEvent } from '../../enums/customEvent'
import { ModalId } from '../../enums/modalId'
import { NotificationKeys } from '../../enums/notificationKeys'
import { dispatchCustomEvent } from '../../utils/customEvent'
import { getOrganizationViewUrl } from '../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../utils/routing/reactRoute'
import { ReviewEntriesBulkValidateConfirmationModal } from './elements/ReviewEntriesBulkValidateConfirmationModal'
import { ReviewEntriesGlobalStatus } from './elements/ReviewEntriesGlobalStatus'
import { ReviewEntriesMonthPeriodFilter } from './elements/ReviewEntriesMonthPeriodFilter'
import { ReviewEntriesSelectionBar } from './elements/ReviewEntriesSelectionBar'
import { ReviewEntriesTable } from './elements/ReviewEntriesTable'
import { ReviewEntryDetailScreen } from './elements/ReviewEntryDetailScreen'
import { getClarificationQuestionKey } from './elements/ReviewEntryDetailScreen/elements/ReviewEntryAiSuggestions/utils/getClarificationQuestionKey'
import { getClarificationQuestions } from './elements/ReviewEntryDetailScreen/elements/ReviewEntryAiSuggestions/utils/getClarificationQuestions'
import { useBulkValidateReviewEntries } from './hooks/useBulkValidateReviewEntries'
import { useReviewEntriesFeature } from './hooks/useReviewEntriesFeature'
import { useReviewEntriesMonthPeriod } from './hooks/useReviewEntriesMonthPeriod'
import { useReviewEntriesSelection } from './hooks/useReviewEntriesSelection'
import { useReviewEntriesTableData } from './hooks/useReviewEntriesTableData'
import { useReviewEntriesTableFilters } from './hooks/useReviewEntriesTableFilters'
import { useReviewEntriesValidation } from './hooks/useReviewEntriesValidation'
import { useReviewEntryDetailPanel } from './hooks/useReviewEntryDetailPanel'
import { getBulkValidateReviewEntriesFromStates } from './utils/getBulkValidateReviewEntriesFromStates'
import { isReviewEntryProcessingStatus } from './utils/isReviewEntryProcessingStatus'

export const ReviewEntries = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { hasOrganization, isEnabled, isLoading: isFeatureLoading } = useReviewEntriesFeature()
  const { dateRange, encodedPeriod, setPeriod } = useReviewEntriesMonthPeriod()
  const { isError, isLoading, items, itemsWithDrafts, rows } = useReviewEntriesTableData(dateRange)
  const sourceRows = useMemo(() => (isError ? [] : rows), [isError, rows])
  const tableFilters = useReviewEntriesTableFilters(sourceRows)
  const sideActions = <ReviewEntriesMonthPeriodFilter encodedPeriod={encodedPeriod} onPeriodChange={setPeriod} />

  const {
    dismissedClarificationKeys,
    dismissedClarificationKeysByEntryId,
    handleDetailClose,
    handleDismissClarification,
    handleNavigateNext,
    handleNavigatePrevious,
    handleRowClick,
    isDetailOpen,
    selectedDocumentId,
    selectedEntryIndex,
    selectedEntryLines,
    selectedEntryRow,
    selectedEntryReference,
    selectedInvoiceNumber,
    selectedJournalCode,
    selectedSupplierId,
    sourceFingerprint,
    totalCount,
  } = useReviewEntryDetailPanel({ items, rows: tableFilters.filteredRows })

  const { accounts, purchaseTaxRates, validationStates } = useReviewEntriesValidation({ items, itemsWithDrafts })

  const automatchedStates = useMemo(() => validationStates.filter((state) => state.isAutomatched), [validationStates])

  // every visible document row is selectable; validatability only gates the validate buttons
  const selectableEntryIds = useMemo(
    () =>
      tableFilters.filteredRows
        .filter((row) => row.showDocumentLevelFields && !isReviewEntryProcessingStatus(row.status))
        .map((row) => row.entryId),
    [tableFilters.filteredRows],
  )

  const { areAllEntriesSelected, clearSelection, selectedEntryIds, toggleAllEntries, toggleEntry } =
    useReviewEntriesSelection(selectableEntryIds)

  const selectedStates = useMemo(
    () => validationStates.filter((state) => selectedEntryIds.includes(state.entryId)),
    [selectedEntryIds, validationStates],
  )

  // questions do not block validation — only an unresolved supplier or VAT rate does
  const isSelectionValidatable = selectedStates.length > 0 && selectedStates.every((state) => state.isValidatable)

  const selectionBlockedCount = useMemo(
    () => selectedStates.filter((state) => !state.isValidatable).length,
    [selectedStates],
  )

  const selectionClarificationCount = useMemo(
    () =>
      selectedStates.filter((state) => {
        const clarifications = getClarificationQuestions(state.item.questions ?? [])
        const dismissedKeys = dismissedClarificationKeysByEntryId[state.entryId] ?? []

        return clarifications.some(
          (question, index) => !dismissedKeys.includes(getClarificationQuestionKey(state.entryId, question, index)),
        )
      }).length,
    [dismissedClarificationKeysByEntryId, selectedStates],
  )

  const { close: closeConfirmModal, open: openConfirmModal } = useModal(
    ModalId.ReviewEntriesBulkValidateConfirmationModal,
  )

  const [bulkValidatedEntryIds, setBulkValidatedEntryIds] = useState<string[]>([])
  const { isProcessing: isBulkValidating, validateEntries } = useBulkValidateReviewEntries({
    onSuccess: (validatedEntryIds) => {
      clearSelection()
      setBulkValidatedEntryIds((current) => [...new Set([...current, ...validatedEntryIds])])
    },
  })

  const handleValidateAllAutomatched = useCallback(() => {
    dispatchCustomEvent(CustomEvent.ReviewEntryDraftFlushRequested)
    const entries = getBulkValidateReviewEntriesFromStates({
      organizationId: organization?.id,
      purchaseTaxRates,
      shouldInclude: (flags) => flags.isAutomatched,
      states: validationStates,
    })

    if (entries.length === 0) {
      notify({
        id: `${NotificationKeys.ReviewEntriesBulkValidate}-error`,
        message: t('review_entries.bulk_validate.error', { count: Math.max(automatchedStates.length, 1) }),
        variant: 'error',
      })
      return
    }

    validateEntries({ accounts, entries, purchaseTaxRates })
  }, [accounts, automatchedStates.length, organization?.id, purchaseTaxRates, t, validateEntries, validationStates])

  const performBulkValidateSelection = useCallback(() => {
    dispatchCustomEvent(CustomEvent.ReviewEntryDraftFlushRequested)
    const entries = getBulkValidateReviewEntriesFromStates({
      organizationId: organization?.id,
      purchaseTaxRates,
      shouldInclude: (flags) => flags.isValidatable,
      states: selectedStates,
    })

    if (entries.length === 0) {
      notify({
        id: `${NotificationKeys.ReviewEntriesBulkValidate}-error`,
        message: t('review_entries.bulk_validate.error', {
          count: Math.max(selectedStates.filter((state) => state.isValidatable).length, 1),
        }),
        variant: 'error',
      })
      return
    }

    validateEntries({ accounts, entries, purchaseTaxRates })
  }, [accounts, organization?.id, purchaseTaxRates, selectedStates, t, validateEntries])

  const handleValidateSelection = useCallback(() => {
    if (selectionClarificationCount > 0) {
      openConfirmModal()
      return
    }

    performBulkValidateSelection()
  }, [openConfirmModal, performBulkValidateSelection, selectionClarificationCount])

  const handleConfirmValidateNow = useCallback(() => {
    closeConfirmModal()
    performBulkValidateSelection()
  }, [closeConfirmModal, performBulkValidateSelection])

  const tableSelection = useMemo(
    () => ({
      areAllEntriesSelected,
      selectableEntryIds,
      selectedEntryIds,
      toggleAllEntries,
      toggleEntry,
    }),
    [areAllEntriesSelected, selectableEntryIds, selectedEntryIds, toggleAllEntries, toggleEntry],
  )

  if (isFeatureLoading) {
    return (
      <ModuleLayout fullWidth sideActions={sideActions} title={t('review_entries.title')}>
        <ReviewEntriesTable isError={false} isLoading sourceRows={sourceRows} tableFilters={tableFilters} />
      </ModuleLayout>
    )
  }

  // no org to build a dashboard URL with — bounce to `/` and let the shell route
  // to the org picker / login
  if (!hasOrganization) {
    return <Navigate to="/" replace />
  }

  // active org but feature flag off (direct navigation via bookmark / history / shared
  // link) — send the user to their dashboard instead of an unroutable `/`
  if (!isEnabled) {
    return (
      <Navigate
        to={getOrganizationViewUrl(organization?.url, reactRoute.organization.dashboard.getMainRoute())}
        replace
      />
    )
  }

  return (
    <ModuleLayout fullWidth sideActions={sideActions} title={t('review_entries.title')}>
      <ReviewEntriesGlobalStatus
        autoValidatableCount={automatchedStates.length}
        isError={isError}
        isLoading={isLoading}
        isValidatingAll={isBulkValidating}
        onValidateAllAutomatched={handleValidateAllAutomatched}
        rows={rows}
      />
      <ReviewEntriesTable
        isError={isError}
        isLoading={isLoading}
        onRowClick={isLoading ? undefined : handleRowClick}
        selection={tableSelection}
        selectionBar={
          <ReviewEntriesSelectionBar
            blockedCount={selectionBlockedCount}
            isProcessing={isBulkValidating}
            isValidateDisabled={!isSelectionValidatable}
            onValidateSelection={handleValidateSelection}
            selectedCount={selectedEntryIds.length}
          />
        }
        sourceRows={sourceRows}
        tableFilters={tableFilters}
      />
      <ReviewEntriesBulkValidateConfirmationModal
        clarificationCount={selectionClarificationCount}
        id={ModalId.ReviewEntriesBulkValidateConfirmationModal}
        onReviewAndClarify={closeConfirmModal}
        onValidateNow={handleConfirmValidateNow}
      />
      {isDetailOpen && selectedEntryRow && (
        <ReviewEntryDetailScreen
          currentIndex={selectedEntryIndex !== null ? selectedEntryIndex + 1 : undefined}
          dismissedClarificationKeys={dismissedClarificationKeys}
          documentId={selectedDocumentId ?? undefined}
          entry={selectedEntryRow}
          entryLines={selectedEntryLines}
          entryReference={selectedEntryReference ?? undefined}
          invoiceNumber={selectedInvoiceNumber ?? undefined}
          isBulkValidating={isBulkValidating}
          isForcedReadOnly={bulkValidatedEntryIds.includes(selectedEntryRow.entryId)}
          journalCode={selectedJournalCode ?? undefined}
          onClose={handleDetailClose}
          onDismissClarification={(questionKey) => handleDismissClarification(selectedEntryRow.entryId, questionKey)}
          onNavigateNext={handleNavigateNext}
          onNavigatePrevious={handleNavigatePrevious}
          sourceFingerprint={sourceFingerprint}
          supplierId={selectedSupplierId ?? undefined}
          totalCount={totalCount}
        />
      )}
    </ModuleLayout>
  )
}
